import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/** Feliratkozó függvény: adatról és hibáról értesít, leiratkozót ad vissza */
export type Subscribe<T> = (onChange: (data: T) => void, onError: (error: Error) => void) => () => void;

interface UseSubscriptionOptions<T> {
  /** false: nincs feliratkozás (pl. amíg nem tudjuk, admin-e a felhasználó) */
  enabled?: boolean;
  /** Szinkron kezdőérték (pl. singleton cache): vele az első adatig sincs töltésjelző */
  initialData?: T | null;
  /** Ha meg van adva, figyelőhibánál toast is jelzi */
  errorMessage?: string;
}

interface UseSubscriptionResult<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Valós idejű forrás (Firestore-figyelő) React-állapotként - a `use*Bookings` és a
 * `usePricingService` közös alapja.
 *
 * A `subscribe` identitása a feliratkozás kulcsa: a hívó `useCallback`-kel rögzíti, és ha a
 * paraméterei változnak (pl. másik felhasználó), új függvényt ad, mire újra feliratkozunk. Az
 * adat és a hiba a saját feliratkozásához kötve tárolódik, így váltáskor nem az előző forrás
 * adata látszik "kész" állapotként.
 *
 * A hibát a következő sikeres adat törli: egy újracsatlakozott figyelő (pl. helyreállt hálózat)
 * után nem maradhat a képernyőn hibaüzenet. Ha egy több forrásból dolgozó feliratkozás tartósan
 * sérült - él, de csak részleges adatot ad -, azt úgy jelzi, hogy minden kiadott adag után újra
 * meghívja az `onError`-t; ilyenkor a hiba végig látszik, a toast viszont csak egyszer szólal meg.
 */
export function useSubscription<T>(
  subscribe: Subscribe<T>,
  { enabled = true, initialData = null, errorMessage }: UseSubscriptionOptions<T> = {},
): UseSubscriptionResult<T> {
  const [state, setState] = useState<{ from: Subscribe<T>; data: T | null; error: Error | null } | null>(null);
  const toastedRef = useRef<string | null>(null);
  const loggedRef = useRef<Error | null>(null);

  useEffect(() => {
    if (!enabled) return;
    toastedRef.current = null;
    loggedRef.current = null;

    return subscribe(
      (data) => setState({ from: subscribe, data, error: null }),
      (error) => {
        // Egy több forrásból dolgozó feliratkozás a tartós hibát minden adag után újra jelzi
        // (lásd a fenti leírást), ugyanazzal a hibával: az a konzolba egyszer kerül, nem minden
        // foglalás-változáskor. Egy ÚJ hiba (egy helyreállás utáni újabb leállás) igen, akkor
        // is, ha az üzenete ugyanaz
        if (loggedRef.current !== error) {
          loggedRef.current = error;
          console.error("Hiba a figyelés közben:", error);
        }
        setState((previous) => ({
          from: subscribe,
          data: previous?.from === subscribe ? previous.data : null,
          error,
        }));
        if (errorMessage && toastedRef.current !== error.message) {
          toastedRef.current = error.message;
          toast.error(errorMessage);
        }
      },
    );
  }, [subscribe, enabled, errorMessage]);

  const own = state?.from === subscribe ? state : null;
  const data = own?.data ?? initialData;
  const error = own?.error ?? null;

  return { data, loading: enabled && data === null && error === null, error };
}
